import React, { useContext, useMemo, useState } from 'react';
import { AdCounterContext } from '@moment/context/AdCounterContext.tsx';
import { createAdComponent } from './ad-utils.tsx';
import { useSearchParams } from 'react-router';
import { AdhesionAd } from '@moment/components/AdhesionAd/AdhesionAd.tsx';
import { DTXAdhesionAd } from '@moment/components/DTXAd/DTXAdhesionAd.tsx';
import { useAds } from '@moment/context/AdsContext.tsx';
import { useTabContainer } from '@moment/layouts/TabContainerLayout/TabContainerContext.tsx';

export const AdPlacementWidget = ({
  placement = 'top',
  adUnitId,
  containerClassName = 'ad--block',
  bannerClassName = 'ad--title',
  adBlockClassName = 'feed__ad'
}: {
  placement?: 'top' | 'bot';
  adUnitId?: string;
  containerClassName?: string;
  bannerClassName?: string;
  adBlockClassName?: string;
}) => {
  const { getAndIncrement } = useContext(AdCounterContext);

  // Use useState for lazy initialization. This ensures the ID is generated ONLY ONCE
  // per component mount, preventing ID drift on re-renders.
  const [stableAdUnitId] = useState(() => adUnitId || `widget-ad-${getAndIncrement()}`);

  const adContent = useMemo(() => {
    const finalAdUnitId = stableAdUnitId;
    const finalPlacement = placement === 'bot' ? 'bot' : 'top';

    // Use shared ad utility
    return createAdComponent({
      adUnitId: finalAdUnitId,
      placement: finalPlacement,
      containerClassName,
      bannerClassName,
      adBlockClassName,
      key: finalAdUnitId,
    });
  }, [placement, stableAdUnitId, containerClassName, bannerClassName, adBlockClassName]);

  return adContent;
};

export const AdhesionAdWidget = ({
  adUnitId,
  hideInApp = false,
}: {
  adUnitId?: string;
  hideInApp?: boolean;
}) => {
  const { getAndIncrement } = useContext(AdCounterContext);
  const [searchParams] = useSearchParams();
  const hideAdhesion = hideInApp && searchParams.get('medium') === 'inapp';
  const { adSource } = useAds();
  const tabContainerContext = useTabContainer();

  const [finalAdUnitId] = useState(() => {
    return adUnitId || `adhesion-ad-${getAndIncrement()}`;
  });

  // If we are inside a TabContainer, calculate the bottom offset.
  const adStyle = useMemo(() => {
    // Only apply the offset if we are in a tab container AND the bar is currently visible.
    if (tabContainerContext?.isInTabContainer && tabContainerContext.isBarVisible) {
      return { bottom: `${tabContainerContext.tabBarHeight}px` };
    }
    return {}; // Otherwise, the ad will default to the bottom of the screen.
  }, [tabContainerContext]);

  const adhesionGamAd = useMemo(() => <AdhesionAd key={"adhesion"} adUnitId={`adh1`} style={adStyle} />, [adStyle]);
  const hasDtx = adSource.includes('dtx');
  const hasGam = adSource.includes('gam');

  // Determine which ad component to use based on adSource
  if (hasDtx && !hideAdhesion) {
    const DTXComponent = DTXAdhesionAd as React.ComponentType<any>;
    return (
      <DTXComponent
        adUnitId={finalAdUnitId}
        style={adStyle}
        fallbackAd={hasGam ? adhesionGamAd : null}
      />
    );
  }
  if (hasGam && !hideAdhesion) {
    return adhesionGamAd;
  }
  return null;
};
